import React, { useEffect, useMemo } from 'react';
import t from 'react-translate';
import { css } from '@emotion/react';

import NvCloneStepIndicator, { CloneStepConfig } from 'shared/components/nv-clone-step-indicator/nv-clone-step-indicator';
import { MentoringProgramEnrollment } from 'redux/schemas/models/mentoring-program-enrollments';
import { useMentoringProgramAdmins } from 'athena/hooks/use-mentoring-program-admins';
import CloneMentorshipProgramStepOne from './steps/clone-mentorship-program-step-one';
import CloneMentorshipProgramStepTwo from './steps/clone-mentorship-program-step-two';
import CloneMentorshipProgramStepThree from './steps/clone-mentorship-program-step-three';

import { useCloneMentorshipProgram } from './state/context';
import CloneMentorshipProgramModalHeader from './clone-mentorship-program-modal-header';
import { CloneMentorshipProgramActionType, CloneMentorshipProgramWizardStep, CloneAdmin } from './state/types';

const mapEnrollmentToCloneAdmin = (enrollment: MentoringProgramEnrollment): CloneAdmin => ({
  id: enrollment.id,
  user: enrollment.user,
});

const CloneMentorshipProgramSteps: Record<CloneMentorshipProgramWizardStep, React.ComponentType> = {
  [CloneMentorshipProgramWizardStep.CONFIG]: CloneMentorshipProgramStepOne,
  [CloneMentorshipProgramWizardStep.ADMIN_REVIEW]: CloneMentorshipProgramStepTwo,
  [CloneMentorshipProgramWizardStep.REVIEW]: CloneMentorshipProgramStepThree,
};

const styles = css`
  .content {
    flex: 1 1 auto;
    min-height: 0;
  }

  .step-content {
    flex: 1 1 auto;
    min-height: 0;
  }

  .step-content.scrollable {
    overflow-y: auto;
  }
`;

type CloneMentorshipProgramModalContentProps = {
  onClose: () => void;
};

const CloneMentorshipProgramModalContent = ({ onClose }: CloneMentorshipProgramModalContentProps) => {
  const { state, dispatch } = useCloneMentorshipProgram();
  const translationBase = t.MENTORING_PROGRAMS.PROGRAM_HEADER_MENU.CLONE.STEPS;
  const stepTwoOptionalLabel = translationBase.STEP_TWO.OPTIONAL_LABEL();

  const programId = state.sourceProgram?.id ? Number(state.sourceProgram.id) : 0;
  const { admins, isLoading } = useMentoringProgramAdmins({ programId });

  // Sync loading state with Context
  useEffect(() => {
    dispatch({ type: CloneMentorshipProgramActionType.SET_FETCHING_ADMINS, payload: isLoading });
  }, [isLoading, dispatch]);

  // Sync retrieved admins with Context
  useEffect(() => {
    if (admins) {
      dispatch({
        type: CloneMentorshipProgramActionType.SET_ADMINS,
        payload: admins.map(mapEnrollmentToCloneAdmin),
      });
    }
  }, [admins, dispatch]);

  const stepsConfig: CloneStepConfig[] = useMemo(() => [{
    name: translationBase.STEP_ONE.NAME(),
    icon: 'content',
    className: 'program-information',
  }, {
    name: translationBase.STEP_TWO.NAME(),
    optionalText: stepTwoOptionalLabel,
    icon: 'groups',
    className: 'user-management',
  }, {
    name: translationBase.STEP_THREE.NAME(),
    icon: 'survey',
    className: 'review',
  }], []); // eslint-disable-line react-hooks/exhaustive-deps

  const Step = CloneMentorshipProgramSteps[state.step as CloneMentorshipProgramWizardStep];

  return (
    <div css={styles} className='h-100 d-flex flex-column'>
      <NvCloneStepIndicator
        currentStep={state.step}
        steps={stepsConfig}
        type='program'
      />
      <div className='p-4 d-flex flex-column content'>
        <CloneMentorshipProgramModalHeader
          step={state.step}
        />
        <div className={`step-content ${state.step === CloneMentorshipProgramWizardStep.ADMIN_REVIEW ? '' : 'scrollable'}`}>
          {Step ? <Step /> : null}
        </div>
      </div>
    </div>
  );
};

export default CloneMentorshipProgramModalContent;
